'use client'

import Image from '@/components/image'
import Link from '@/components/link-locale'
import Show from '@/components/show'
import { siteConfig } from '@/config/site'
import { useRouter } from '@/i18n/routing'
import { GetCategoryList } from '@/lib/api/hooks/use-category'
import { useMyChannels } from '@/lib/api/hooks/use-channel'
import { GetUser } from '@/lib/api/hooks/use-user'
import { useCreatorStore } from '@/store'
import { useGuestStore } from '@/store'
import { getAssetUrl } from '@/utils'
import { Avatar, Input, Select } from 'antd'
import clsx from 'clsx'
import { useLocale, useTranslations } from 'next-intl'
import { usePathname, useSearchParams } from 'next/navigation'
import { pathOr } from 'ramda'
import React, { useMemo, useState } from 'react'
import { AiOutlineGlobal, AiOutlineSearch } from 'react-icons/ai'

export default function NavBar() {
  const [keyword, setKeyword] = useState('')
  const { selectedTags, setSelectedTags } = useGuestStore()
  const router = useRouter()
  const locale = useLocale()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const t = useTranslations()

  const queryString = searchParams.toString()
  const currentHref = pathname + (queryString ? `?${queryString}` : '')

  const relativePath = pathname.replace(`/${locale}`, '/').replace('//', '/')

  const { data: user } = GetUser()
  const isLoggedIn = user?.isLogin
  const { data: channels } = useMyChannels()
  const { data: categories, isLoading: isLoadingCategories } = GetCategoryList()

  const options = useMemo(() => {
    const tags = pathOr([], ['dataSource', '0', 'categories'], categories)

    return tags
      .map(item => ({
        value: item.id,
        label: t(item.i18nKey),
      }))
      .filter(item => {
        const withoutSingleGender = !siteConfig.blackList.categoryIds.includes(
          item.value,
        )
        return withoutSingleGender
      })
  }, [categories, t])

  const channelId = useCreatorStore(state => state.channelId)

  const channel = useMemo(() => {
    if (channels) {
      return channels.list.find(item => item.id === channelId)
    }
  }, [channels, channelId])

  const href = useMemo(() => {
    if (user && channelId) {
      return '/creator/channel'
    }
    if (user) {
      return '/user/profile'
    }
    return `/login?from=${currentHref}`
  }, [user, channelId, currentHref])

  const avatar = useMemo(() => {
    if (channel) {
      return channel.avatar || getAssetUrl(siteConfig.placeholder.avatar)
    }
    if (user) {
      return user.avatar || getAssetUrl(siteConfig.placeholder.avatar)
    }
    return getAssetUrl(siteConfig.placeholder.avatar)
  }, [channel, user])

  const onSearch = () => {
    const q = encodeURIComponent(keyword.trim())
    if (!q) {
      return
    }
    setKeyword('')
    router.push(`/search?q=${q}`)
  }

  const handleLanguageChange = value => {
    const href = pathname.replace(`/${locale}`, '/').replace('//', '/')
    router.replace(href, { locale: value })
  }

  const handleOptionSelect = value => {
    setSelectedTags(value)
  }

  return (
    <div className='fixed left-0 right-0 top-0 z-30 grid h-20 w-dvw grid-cols-3 items-center justify-between bg-[#27272E] px-[25px] lg:static lg:flex lg:px-[70px]'>
      <Link href={href} className='lg:hidden'>
        <Avatar size={32} shape='circle' className='bg-black/50' src={avatar} />
      </Link>
      <Link href='/' className='flex justify-center'>
        <Image
          src='/logo.svg'
          alt='Mfans'
          width={327}
          height={120}
          className='h-8 w-auto lg:h-10'
        />

        <div className='flex w-[26.2rem] items-center pl-8'>
          {!isLoadingCategories && !user && (
            <Select
              className='w-[6rem]'
              placeholder='篩選性向'
              options={options}
              value={selectedTags[0]}
              onChange={handleOptionSelect}
            />
          )}
        </div>
      </Link>
      <div className='hidden items-center gap-x-2.5 text-sm font-semibold lg:flex'>
        <Show when={isLoggedIn}>
          <Link href='/'>
            <div
              className={clsx([
                'flex h-20 w-28 items-center justify-center border-primary text-white hover:border-b-4 hover:text-primary',
                { 'border-b-4 text-primary': relativePath === '/' },
              ])}
            >
              <span>{t('header.home')}</span>
            </div>
          </Link>
          <Link href='/feed/videos'>
            <div
              className={clsx([
                'flex h-20 w-28 items-center justify-center border-primary text-white hover:border-b-4 hover:text-primary',
                { 'border-b-4 text-primary': relativePath === '/feed/videos' },
              ])}
            >
              <span>{t('header.category')}</span>
            </div>
          </Link>
        </Show>
      </div>
      <div className='flex justify-end gap-x-5'>
        <Show when={isLoggedIn}>
          <Input
            className='hidden h-10 w-[280px] rounded-md bg-black/50 lg:flex'
            placeholder={t('header.searchPlaceholder')}
            suffix={
              <AiOutlineSearch
                className='cursor-pointer text-2xl'
                onClick={onSearch}
              />
            }
            onChange={e => setKeyword(e.target.value)}
            onPressEnter={onSearch}
          />
        </Show>
        <Select
          className='h-9 w-24 rounded-xl bg-black/50 lg:h-10'
          defaultValue={locale}
          options={[
            { value: 'zh-TW', label: '繁中' },
            { value: 'zh-CN', label: '简中' },
            { value: 'en-US', label: 'English' },
          ]}
          suffixIcon={null}
          prefix={<AiOutlineGlobal className='text-xl' />}
          onChange={handleLanguageChange}
        />
      </div>
    </div>
  )
}
